// Portal-based routing configuration

/**
 * Public booking route for unauthenticated usersss
 */
export const PUBLIC_BOOKING_ROUTE = "/bookings" as const;

export const PORTAL_ROUTES = {
  superadmin: {
    base: "/superadmin",
    dashboard: "/superadmin/dashboard",
    inbox: "/superadmin/inbox",
    users: "/superadmin/users",
    venues: "/superadmin/venues",
    settings: "/superadmin/settings",
    reports: "/superadmin/reports",
    venue: (venueId: string) => ({
      base: `/superadmin/venue/${venueId}`,
      dashboard: `/superadmin/venue/${venueId}/dashboard`,
      studios: `/superadmin/venue/${venueId}/studios`,
      shows: `/superadmin/venue/${venueId}/shows`,
      partners: `/superadmin/venue/${venueId}/partners`,
      bookings: `/superadmin/venue/${venueId}/bookings`,
      customers: `/superadmin/venue/${venueId}/customers`,
      invoices: `/superadmin/venue/${venueId}/invoices`,
      holidays: `/superadmin/venue/${venueId}/holidays`,
      addOns: `/superadmin/venue/${venueId}/add-ons`,
      shopItems: `/superadmin/venue/${venueId}/shop-items`,
      priceModifiers: `/superadmin/venue/${venueId}/price-modifiers`,
      faqs: `/superadmin/venue/${venueId}/faqs`,
      settings: `/superadmin/venue/${venueId}/settings`,
    }),
  },
  booking: {
    base: "/booking",
    dashboard: "/booking/dashboard",
    inbox: "/booking/inbox",
    users: "/booking/users",
    venue: (venueId: string) => ({
      base: `/booking/venue/${venueId}`,
      dashboard: `/booking/venue/${venueId}/dashboard`,
      bookings: `/booking/venue/${venueId}/bookings`,
      customers: `/booking/venue/${venueId}/customers`,
      invoices: `/booking/venue/${venueId}/invoices`,
      studios: `/booking/venue/${venueId}/studios`,
      shows: `/booking/venue/${venueId}/shows`,
      invoicing: `/booking/venue/${venueId}/invoicing`,
      reports: `/booking/venue/${venueId}/reports`,
      priceModifiers: `/booking/venue/${venueId}/price-modifiers`,
      holidays: `/booking/venue/${venueId}/holidays`,
      addOns: `/booking/venue/${venueId}/add-ons`,
      shopItems: `/booking/venue/${venueId}/shop-items`,
      faqs: `/booking/venue/${venueId}/faqs`,
    }),
  },
  barstaff: {
    base: "/barstaff",
    dashboard: "/barstaff/dashboard",
    venue: (venueId: string) => ({
      base: `/barstaff/venue/${venueId}`,
      kiosk: `/barstaff/venue/${venueId}/kiosk`,
      todaysBookings: `/barstaff/venue/${venueId}/todays-bookings`,
      booking: `/barstaff/venue/${venueId}/booking`,
      dashboard: `/barstaff/venue/${venueId}/dashboard`,
    }),
  },
  customerportal: {
    base: "/customerportal",
    dashboard: "/customerportal/dashboard",
    bookings: "/customerportal/bookings",
    profile: "/customerportal/profile",
    newBooking: "/customerportal/new-booking",
    booking: (bookingId: string) => `/customerportal/bookings/${bookingId}`,
  },
  guest: {
    base: "/guest",
    booking: "/guest/booking",
    confirm: "/guest/booking/confirm",
  },
} as const;

// Helper function to get portal-aware routes
export function getPortalRoute(
  role: string,
  path: string,
  params?: Record<string, string>,
) {
  switch (role) {
    case "super_admin":
      return buildRoute(PORTAL_ROUTES.superadmin, path, params);
    case "booking_staff":
      return buildRoute(PORTAL_ROUTES.booking, path, params);
    case "bar_staff":
      return buildRoute(PORTAL_ROUTES.barstaff, path, params);
    case "customer":
      return buildRoute(PORTAL_ROUTES.customerportal, path, params);
    case "guest_user":
      return buildRoute(PORTAL_ROUTES.guest, path, params);
    case "readonly":
      return buildRoute(PORTAL_ROUTES.barstaff, path, params);
    default:
      return "/login";
  }
}

// Helper to build routes with parameters
function buildRoute(
  portal: any,
  path: string,
  params?: Record<string, string>,
) {
  let route = portal.base;

  if (params?.venueId && portal.venue) {
    const venueRoutes = portal.venue(params.venueId);
    const pathKey = path.replace("venue/", "");
    route = venueRoutes[pathKey] || venueRoutes.base;
  } else {
    route = portal[path] || portal.base;
  }

  return route;
}

// Get default portal route based on role
export function getDefaultPortal(role: string): string {
  switch (role) {
    case "super_admin":
      return PORTAL_ROUTES.superadmin.dashboard;
    case "booking_staff":
      return PORTAL_ROUTES.booking.dashboard;
    case "bar_staff":
      return PORTAL_ROUTES.barstaff.dashboard;
    case "customer":
      return PORTAL_ROUTES.customerportal.bookings;
    case "guest_user":
      return PORTAL_ROUTES.guest.booking;
    case "readonly":
      return PORTAL_ROUTES.barstaff.dashboard;
    default:
      return "/login";
  }
}

// Check if a path belongs to a specific portal
export function isPortalPath(
  path: string,
  portal: "superadmin" | "booking" | "barstaff" | "customerportal" | "guest",
): boolean {
  return path.startsWith(PORTAL_ROUTES[portal].base);
}

// Extract venue ID from current path
export function extractVenueIdFromPath(path: string): string | null {
  const match = path.match(/\/venue\/(\d+)/);
  return match ? match[1] : null;
}
